import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, AgentJsonBox } from "@/components/admin/Ui";
import { MagnifyingGlass, Sparkle } from "@phosphor-icons/react";

const REGION_OPTIONS = ["Europe", "Adriatic", "Balkans", "CEE", "Türkiye", "Middle East", "Asia", "USA"];
const SERVICE_OPTIONS = ["Air Freight", "Sea Freight", "Project Cargo", "Heavy Lift", "Oil & Gas"];

export default function Research() {
  const [form, setForm] = useState({
    target_regions: ["Europe", "Adriatic"],
    target_services: ["Sea Freight", "Air Freight"],
    notes: "",
    max_results: 6,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (key, v) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v],
    }));

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/leads/research", form);
      setResult(data);
      toast.success(`${data.inserted_count} leads created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Research failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-research-page">
      <PageHeader title="Lead Research Agent" subtitle="Agent 1 · GPT-5" />
      <div className="p-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white border border-black/10 p-6 space-y-5">
          <div>
            <div className="overline mb-3">Target regions</div>
            <div className="flex flex-wrap gap-2">
              {REGION_OPTIONS.map((r) => (
                <button
                  key={r}
                  data-testid={`research-region-${r.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => toggle("target_regions", r)}
                  className={`text-xs uppercase tracking-widest px-3 py-2 border transition-colors ${
                    form.target_regions.includes(r) ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-black/15 hover:border-navy"
                  }`}
                >{r}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-3">Target services</div>
            <div className="flex flex-wrap gap-2">
              {SERVICE_OPTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`research-service-${s.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                  onClick={() => toggle("target_services", s)}
                  className={`text-xs uppercase tracking-widest px-3 py-2 border transition-colors ${
                    form.target_services.includes(s) ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-black/15 hover:border-navy"
                  }`}
                >{s}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-2">Notes for the agent</div>
            <textarea
              data-testid="research-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. focus on mid-size independent forwarders with project cargo capability"
              className="w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            />
          </div>
          <div>
            <div className="overline mb-2">Max results</div>
            <input
              data-testid="research-max"
              type="number" min="1" max="20"
              value={form.max_results}
              onChange={(e) => setForm({ ...form, max_results: Number(e.target.value) })}
              className="w-24 border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            />
          </div>
          <button
            data-testid="research-run-button"
            onClick={run}
            disabled={loading}
            className="btn-primary w-full justify-center"
          >
            {loading ? <>Researching…</> : <><MagnifyingGlass size={16} /> Run Research Agent</>}
          </button>
        </div>

        <div className="bg-white border border-black/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={16} weight="duotone" className="text-ember" />
            <div className="overline">Agent output</div>
          </div>
          {loading && (
            <div className="flex items-center gap-3 text-sm text-navy/70" data-testid="research-loading">
              <div className="w-3 h-3 rounded-full bg-ember animate-pulse" />
              Researching — GPT-5 is compiling candidate forwarders… 30–60s.
            </div>
          )}
          {!result && !loading && <div className="text-sm text-navy/50">Run the agent to see structured output.</div>}
          {result && (
            <>
              <div className="mb-3 text-sm text-navy">
                <span className="font-semibold">{result.inserted_count}</span> leads inserted into the pipeline.
              </div>
              <AgentJsonBox data={result.raw} testId="research-agent-output" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
